import { ChevronRight, Pin, Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { Avatar } from '../components/Avatar.js';
import { Kbd } from '../components/Kbd.js';
import { Monogram } from '../components/Monogram.js';
import { Tooltip } from '../components/Tooltip.js';
import { Logo } from '../brand/Logo.js';
import { cn } from '../lib/cn.js';
import { fmtCount } from '../format.js';

export interface RailAccountItem {
  id: string;
  label: string;
  /** Two-letter code for the square monogram. */
  code: string;
  pinned?: boolean;
  active?: boolean;
  href?: string;
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
  /** Mono hint on the right (provider, freshness). */
  hint?: string;
  accent?: 'brand' | 'warn' | 'neutral';
}

export interface RailItem {
  id: string;
  label: string;
  icon: LucideIcon;
  badge?: number | string;
  active?: boolean;
  href?: string;
  onSelect?: (event: MouseEvent<HTMLElement>) => void;
  /** Visual scaffolding: rendered as a disabled button, out of the tab order. */
  disabled?: boolean;
  /** Nested account items (indented, monogram). */
  children?: RailAccountItem[];
  /** Collapse nested children. */
  collapsed?: boolean;
  onToggle?: () => void;
  /** Mono shortcut hint shown in tooltips when collapsed. */
  shortcut?: string;
}

export interface RailGroup {
  id: string;
  /** Mono uppercase group label. */
  label: string;
  items: RailItem[];
}

export interface RailFooter {
  name: string;
  /** Second line: organisation or role. */
  org: string;
  initials?: string;
  onSelect?: () => void;
}

export interface LeftRailProps {
  /** Rail width in px, 220–340 (spec section 22). Default 272 since the appearance change (spec 248). Ignored when collapsed. */
  width?: number;
  /** 56px icon-only mode. */
  collapsed?: boolean;
  groups: RailGroup[];
  /** Opens the command palette. The affordance is a real button with a ⌘K hint. */
  onSearch?: () => void;
  searchLabel?: string;
  footer?: RailFooter;
  /** Optional slot under the logo band (environment badge, account picker). */
  topSlot?: ReactNode;
  /** Logo click target. */
  onLogoSelect?: () => void;
  className?: string;
}

export const RAIL_MIN_WIDTH = 220;
export const RAIL_MAX_WIDTH = 340;
export const RAIL_DEFAULT_WIDTH = 272;
export const RAIL_COLLAPSED_WIDTH = 56;

export function clampRailWidth(width: number): number {
  return Math.round(Math.min(RAIL_MAX_WIDTH, Math.max(RAIL_MIN_WIDTH, width)));
}

/**
 * Left navigation rail (DESIGN_SYSTEM section 7.1): bg --panel, right border --line, logo band h-14
 * with an 18px lockup, search affordance, mono group labels, 13px items with 14px icons, active =
 * --card + 2px brand bar, badge counts, nested account items, footer avatar.
 */
export function LeftRail({ width = RAIL_DEFAULT_WIDTH, collapsed = false, groups, onSearch, searchLabel = 'Search', footer, topSlot, onLogoSelect, className }: LeftRailProps) {
  const w = collapsed ? RAIL_COLLAPSED_WIDTH : clampRailWidth(width);
  return (
    <nav
      aria-label="Primary"
      data-collapsed={collapsed || undefined}
      style={{ width: w, minWidth: w }}
      className={cn('flex h-full shrink-0 flex-col border-r border-line bg-panel transition-[width] duration-200 motion-reduce:transition-none', className)}
    >
      <div className={cn('flex h-14 shrink-0 items-center border-b border-line', collapsed ? 'justify-center px-0' : 'px-4')}>
        {onLogoSelect ? (
          <button type="button" onClick={onLogoSelect} aria-label="Agency home" className="rounded-sm">
            <Logo variant={collapsed ? 'mark' : 'lockup'} height={18} tone={collapsed ? 'brand' : 'white'} title="" />
          </button>
        ) : (
          <Logo variant={collapsed ? 'mark' : 'lockup'} height={18} tone={collapsed ? 'brand' : 'white'} />
        )}
      </div>
      {topSlot ? <div className={cn('shrink-0', collapsed ? 'px-2 pt-3' : 'px-3 pt-3')}>{topSlot}</div> : null}
      {onSearch ? (
        <div className={cn('shrink-0', collapsed ? 'px-2 pt-4' : 'px-3 pt-4')}>
          {collapsed ? (
            <Tooltip content={searchLabel} shortcut="⌘K" side="right">
              <button
                type="button"
                onClick={onSearch}
                aria-label={`${searchLabel} (⌘K)`}
                className="flex h-8 w-full items-center justify-center rounded-sm border border-line bg-bg text-mute transition-colors hover:text-ink"
              >
                <Search size={12} aria-hidden />
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={onSearch}
              aria-label={`${searchLabel} (⌘K)`}
              aria-keyshortcuts="Meta+K"
              className="flex w-full items-center gap-2 rounded-sm border border-line bg-bg px-2 py-1.5 text-left transition-colors hover:border-mute/60"
            >
              <Search size={12} aria-hidden className="shrink-0 text-mute" />
              <span className="flex-1 truncate text-xs text-mute">{searchLabel}</span>
              <Kbd>⌘K</Kbd>
            </button>
          )}
        </div>
      ) : null}
      <div className={cn('@container scrollbar-thin mt-4 min-h-0 flex-1 overflow-y-auto pb-4', collapsed ? 'px-[8px]' : 'px-[12px]')}>
        {groups.map((group) => (
          <div key={group.id} className="mb-5 last:mb-0" role="group" aria-label={group.label}>
            {collapsed ? (
              <div aria-hidden className="mx-auto mb-2 h-px w-4 bg-hair" />
            ) : (
              <p className="px-2 pb-2 font-mono text-2xs uppercase tracking-[0.2em] text-mute">{group.label}</p>
            )}
            <ul className="flex flex-col gap-px">
              {group.items.map((item) => (
                <RailNavItem key={item.id} item={item} collapsed={collapsed} />
              ))}
            </ul>
          </div>
        ))}
      </div>
      {footer ? (
        <div className={cn('shrink-0 border-t border-line', collapsed ? 'flex justify-center px-2 py-3' : 'px-4 py-3')}>
          {footer.onSelect ? (
            <button type="button" onClick={footer.onSelect} aria-label={`${footer.name}, ${footer.org}`} className={cn('flex w-full items-center gap-3 rounded-sm text-left', collapsed ? 'justify-center' : '')}>
              <FooterBody footer={footer} collapsed={collapsed} />
            </button>
          ) : (
            <div className={cn('flex items-center gap-3', collapsed ? 'justify-center' : '')}>
              <FooterBody footer={footer} collapsed={collapsed} />
            </div>
          )}
        </div>
      ) : null}
    </nav>
  );
}

function FooterBody({ footer, collapsed }: { footer: RailFooter; collapsed: boolean }) {
  return (
    <>
      <Avatar name={footer.name} initials={footer.initials} size={28} />
      {!collapsed ? (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs text-ink">{footer.name}</span>
          <span className="block truncate text-2xs text-mute">{footer.org}</span>
        </span>
      ) : null}
    </>
  );
}

const ITEM_BASE = 'group relative flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-nav transition-colors';
const ITEM_IDLE = 'text-mute hover:bg-card hover:text-ink';
const ITEM_ACTIVE = 'bg-card text-ink';

function RailNavItem({ item, collapsed }: { item: RailItem; collapsed: boolean }) {
  const Icon = item.icon;
  const hasChildren = Boolean(item.children && item.children.length > 0);
  const inner = (
    <>
      {item.active ? <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] bg-brand" /> : null}
      <Icon size={14} strokeWidth={1.5} aria-hidden className="shrink-0" />
      {!collapsed ? (
        <>
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.badge !== undefined ? (
            <span className="rounded-sm bg-bg px-1.5 py-0.5 font-mono text-2xs tabular-nums text-mute">{typeof item.badge === 'number' ? fmtCount(item.badge) : item.badge}</span>
          ) : null}
          {hasChildren && item.onToggle ? (
            <ChevronRight size={11} aria-hidden className={cn('shrink-0 text-dim transition-transform motion-reduce:transition-none', item.collapsed ? '' : 'rotate-90')} />
          ) : null}
        </>
      ) : null}
    </>
  );
  const className = cn(ITEM_BASE, item.active ? ITEM_ACTIVE : ITEM_IDLE, collapsed ? 'justify-center px-0' : '', item.disabled ? 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-mute' : '');
  let control: ReactNode;
  if (item.disabled) {
    control = (
      <button type="button" disabled aria-disabled tabIndex={-1} className={className}>
        {inner}
      </button>
    );
  } else if (item.href) {
    control = (
      <a href={item.href} onClick={item.onSelect} aria-current={item.active ? 'page' : undefined} className={className} aria-label={collapsed ? item.label : undefined}>
        {inner}
      </a>
    );
  } else {
    control = (
      <button
        type="button"
        onClick={(e) => {
          item.onSelect?.(e);
          if (hasChildren) item.onToggle?.();
        }}
        aria-current={item.active ? 'page' : undefined}
        aria-expanded={hasChildren && item.onToggle ? !item.collapsed : undefined}
        className={className}
        aria-label={collapsed ? item.label : undefined}
      >
        {inner}
      </button>
    );
  }
  const wrapped = collapsed ? (
    <Tooltip content={item.label} shortcut={item.shortcut} side="right">
      {control}
    </Tooltip>
  ) : (
    control
  );
  return (
    <li>
      {wrapped}
      {hasChildren && !collapsed && !item.collapsed ? (
        <ul className="mt-px flex flex-col gap-px pl-4" aria-label={`${item.label} accounts`}>
          {item.children?.map((acct) => (
            <li key={acct.id}>
              <AccountRow item={acct} />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function AccountRow({ item }: { item: RailAccountItem }) {
  const className = cn('group relative flex w-full items-center gap-2 rounded-sm py-1 pl-2 pr-2 text-left text-table transition-colors', item.active ? ITEM_ACTIVE : ITEM_IDLE);
  const inner = (
    <>
      {item.active ? <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] bg-brand" /> : null}
      <Monogram code={item.code} accent={item.accent ?? 'neutral'} size="sm" className="!h-5 !w-5 !text-[9px]" label="" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.hint ? <span className="shrink-0 font-mono text-2xs text-mute">{item.hint}</span> : null}
      {item.pinned ? <Pin size={10} aria-label="Pinned" className="shrink-0 text-mute" /> : null}
    </>
  );
  if (item.href) {
    return (
      <a href={item.href} onClick={item.onSelect} aria-current={item.active ? 'page' : undefined} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={item.onSelect} aria-current={item.active ? 'page' : undefined} className={className}>
      {inner}
    </button>
  );
}
